const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface ApiResponse<T> {
  data?: T
  error?: string
}

async function adminRequest<T>(endpoint: string, token: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })

    const data = await response.json()

    if (!response.ok) {
      return { error: data.error || 'Something went wrong' }
    }

    return { data }
  } catch (error) {
    console.error('Admin API error:', error)
    return { error: 'Unable to connect to server' }
  }
}

// Types

// Base fields shared by all submissions
interface BaseSubmission {
  id: string
  email: string
  createdAt: string
  includesPeople?: boolean
  hasModelPermission?: boolean
}

// Draft submissions - in progress, fields may be incomplete
export interface DraftSubmission extends BaseSubmission {
  status: 'draft'
  name?: string
  grade?: string
  photoTitle?: string
  photoUrl?: string
}

// Finalized submissions - complete, all required fields present
export interface FinalizedSubmission extends BaseSubmission {
  status: 'pending' | 'approved' | 'rejected'
  name: string
  grade: string
  photoTitle: string
  photoUrl: string
  rejectionReason?: string
  moderatedAt?: string
  moderatedBy?: string
}

// Discriminated union - TypeScript narrows based on status
export type Submission = DraftSubmission | FinalizedSubmission

// Type guard for checking if submission is finalized
export function isFinalized(s: Submission): s is FinalizedSubmission {
  return s.status !== 'draft'
}

export interface Competition {
  phase: string
  name: string
}

export interface VoteResult {
  submissionId: string
  submission?: {
    name: string
    email: string
    grade: string
    photoTitle: string
    photoUrl?: string
  }
  firstChoiceCount: number
  secondChoiceCount: number
  thirdChoiceCount: number
  totalPoints: number
}

// API functions
export async function getSubmissions(token: string) {
  return adminRequest<{
    submissions: Submission[]
    competition: Competition
  }>('/api/admin/submissions', token)
}

export async function approveSubmission(token: string, submissionId: string) {
  return adminRequest<{ message: string }>(`/api/admin/submissions/${submissionId}/approve`, token, { method: 'PUT' })
}

export async function rejectSubmission(token: string, submissionId: string, reason?: string) {
  return adminRequest<{ message: string }>(`/api/admin/submissions/${submissionId}/reject`, token, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  })
}

export async function deleteSubmission(token: string, submissionId: string) {
  return adminRequest<{ message: string }>(`/api/admin/submissions/${submissionId}`, token, { method: 'DELETE' })
}

export async function publishPhotos(token: string) {
  return adminRequest<{ message: string; count: number }>('/api/admin/publish', token, { method: 'POST' })
}

export async function updatePhase(token: string, phase: string) {
  return adminRequest<{ message: string; phase: string }>('/api/admin/competition/phase', token, {
    method: 'PUT',
    body: JSON.stringify({ phase }),
  })
}

export async function getResults(token: string) {
  return adminRequest<{ results: VoteResult[] }>('/api/admin/results', token)
}

// Individual Votes
export interface IndividualVote {
  email: string
  votedAt: string
  firstChoice: { id: string; photoTitle: string; name: string }
  secondChoice: { id: string; photoTitle: string; name: string }
  thirdChoice: { id: string; photoTitle: string; name: string }
}

export async function getVotes(token: string) {
  return adminRequest<{ votes: IndividualVote[]; totalVotes: number }>('/api/admin/votes', token)
}

// Voting Config
export interface VotingConfig {
  votingMode: 'classic' | 'elo'
  eloComparisonsPerVoter: number
  phase: string
}

export interface EloRanking {
  rank: number
  submissionId: string
  eloScore: number
  matchCount: number
  exposureCount: number
  submitterName?: string
  photoTitle?: string
  photoUrl?: string
}

export async function getVotingConfig(token: string) {
  return adminRequest<VotingConfig>('/api/admin/voting/config', token)
}

export async function updateVotingConfig(
  token: string,
  votingMode: 'classic' | 'elo',
  eloComparisonsPerVoter?: number
) {
  return adminRequest<{
    message: string
    votingMode: 'classic' | 'elo'
    eloComparisonsPerVoter: number
  }>('/api/admin/voting/config', token, {
    method: 'PUT',
    body: JSON.stringify({ votingMode, eloComparisonsPerVoter }),
  })
}

export async function initializeEloScores(token: string) {
  return adminRequest<{ message: string; count: number }>('/api/admin/voting/initialize', token, { method: 'POST' })
}

export async function getEloRankings(token: string) {
  return adminRequest<{ rankings: EloRanking[] }>('/api/admin/voting/elo-rankings', token)
}
